'use client';

import { c as _c } from "react/compiler-runtime";
import { Component, type ErrorInfo, type ReactNode, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useIsAnonAuthPending, useIsAnonAuthed } from '@lib/auth/contexts/anon-auth-context';
import { useClerkDegraded } from '@lib/auth/hooks/use-clerk-degraded';
import { isAuthRoutePath } from '@lib/auth/utils/is-auth-route-path';
import { isSameOriginApiFetch } from '@lib/auth/utils/should-attach-bearer';
import { ALL_ROUTES, AUTO_SIGNIN_PREFIXES } from '@lib/constants/apis';
import { API_401_HEADER } from '@lib/observability/auth-401-tags/constants';
import { captureException } from '@lib/observability/errors/capture-exception.client';
import { getLogger } from '@lib/observability/logging/log.client';
import { clearQueryCacheBeforeAuthRedirect } from '@lib/query/query-client';
import { buildSignInRedirect } from '@lib/shared/components/invitation-page/utils/build-sign-in-redirect';
import { ErrorFeedbackCard } from './error-feedback-card';
import { hasPromptedThisSession } from './feedback-prompt-dedupe';
const {
  logInfo
} = getLogger('components/error-boundary');

/**
 * Cancel in-flight TanStack work, then hard-navigate to login.
 * Soft `router.push` kept the QueryClient alive across auth loss;
 * full unload replaces the JS realm. We cancel (not clear) first so a
 * cancelled `beforeunload` prompt cannot leave an emptied live cache
 * on the protected page.
 */
function redirectToLoginAfterCacheClear(loginUrl: string): void {
  void clearQueryCacheBeforeAuthRedirect().finally(() => {
    window.location.href = loginUrl;
  });
}
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  isClerkDegraded?: boolean;
}
interface State {
  hasError: boolean;
  error?: Error;
  authRedirectInitiated: boolean;
  eventId?: string;
  /**
   * `name: message` of the error the last retry tried to clear, held
   * only while that retry has yet to reach a healthy commit.
   * Rendering the fallback already unmounts the children, so a retry
   * mounts them fresh — enough for a transient fault. Facing the same
   * signature again means a fresh mount does not reach it: the live
   * DOM is out of step with what React committed, and only a new
   * document clears that.
   */
  lastResetErrorSignature?: string;
}

// Type for potential error objects that might have status properties
interface ErrorWithStatus {
  status?: number;
  message?: string;
  response?: {
    status?: number;
  };
  name?: string;
}

/**
 * Class-based Error Boundary that catches React errors including 401 auth errors
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      authRedirectInitiated: false
    };
  }
  static getDerivedStateFromError(error: Error): Pick<State, 'hasError' | 'error' | 'authRedirectInitiated'> {
    // For auth errors, initialize authRedirectInitiated to true to prevent
    // flashing the generic error UI before componentDidCatch fires the redirect.
    const isAuth = ErrorBoundary.isAuthErrorStatic(error);
    return {
      hasError: true,
      error,
      authRedirectInitiated: isAuth
    };
  }

  /**
   * Static version for use in getDerivedStateFromError (which cannot access `this`).
   * Delegates to the standalone isAuthError to avoid duplicated logic.
   */
  private static isAuthErrorStatic(error: Error): boolean {
    return isAuthError(error);
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Single capture path. Do NOT also call logError(error) — logError re-routes
    // through reportError → Sentry+PostHog providers, which would duplicate the
    // capture we just did. React itself logs boundary errors to the dev console.
    const eventId = captureException(error, {
      $exception_source: 'react_error_boundary',
      component_stack: errorInfo.componentStack
    });
    this.setState({
      eventId
    });

    // Check if it's a 401 authentication error — but don't redirect during Clerk outage
    if (this.isAuthError(error)) {
      if (this.props.isClerkDegraded) {
        logInfo('Suppressed 401 redirect — Clerk is degraded');
      } else {
        this.handleAuthError();
      }
    }
  }
  componentDidUpdate() {
    // A commit that lands with the children mounted and no error means
    // the retry worked. Drop the signature there: this boundary wraps
    // whole route groups, so holding it would make an unrelated failure
    // minutes later reload on its own first retry and take the signer's
    // unsaved entry with it. A crash that survives the retry throws
    // during that same commit, before this runs, so the escalation path
    // still sees its signature.
    if (!this.state.hasError && this.state.lastResetErrorSignature) {
      this.setState({
        lastResetErrorSignature: undefined
      });
    }
  }
  private handleReset = () => {
    const signature = ErrorBoundary.errorSignature(this.state.error);

    // The previous retry already mounted the children fresh and the
    // same error returned, so the stale DOM outlives the subtree that
    // owned it. Escalation stays tied to the user's own retry — never
    // automatic — so nothing reloads under a signer mid-entry.
    if (signature !== undefined && signature === this.state.lastResetErrorSignature && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    this.setState({
      hasError: false,
      error: undefined,
      authRedirectInitiated: false,
      eventId: undefined,
      lastResetErrorSignature: signature
    });
  };

  /**
   * `name` alone collides across unrelated failures — most thrown
   * errors are plain `Error`. Pairing it with the message keeps the
   * escalation aimed at one recurring fault.
   */
  private static errorSignature(error: Error | undefined): string | undefined {
    return error ? `${error.name}: ${error.message}` : undefined;
  }
  private isAuthError(error: Error): boolean {
    return ErrorBoundary.isAuthErrorStatic(error);
  }
  private isOnAuthRoute(): boolean {
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    return isAuthRoutePath(path);
  }
  private handleAuthError() {
    // Suppress: redirect would loop on auth routes.
    if (this.isOnAuthRoute()) return;
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    const loginUrl = `${ALL_ROUTES.LOGIN}?redirectedFrom=${encodeURIComponent(currentPath)}`;
    if (typeof window !== 'undefined') {
      this.setState({
        authRedirectInitiated: true
      });
      redirectToLoginAfterCacheClear(loginUrl);
    }
  }
  render() {
    if (this.state.hasError) {
      const isAuth = this.state.error && this.isAuthError(this.state.error);
      const onAuthRoute = this.isOnAuthRoute();

      // Auth error + Clerk healthy + NOT already on an auth route → redirect.
      // On auth routes we fall through to the fallback UI; redirecting would loop.
      if (isAuth && !this.props.isClerkDegraded && !onAuthRoute) {
        // Clerk may have recovered from degraded state after componentDidCatch
        // suppressed the redirect. Trigger redirect now that Clerk is healthy.
        if (!this.state.authRedirectInitiated) {
          this.handleAuthError();
        }
        return null;
      }

      // User-provided fallback wins over both the card and the slim
      // fallback — preserves the pre-existing override behavior.
      if (this.props.fallback) return this.props.fallback;

      // Dedupe consumed earlier this tab session → skip straight to the slim UI.
      if (hasPromptedThisSession()) {
        return <SlimFallback onTryAgain={this.handleReset} data-sentry-element="SlimFallback" data-sentry-component="ErrorBoundary" data-sentry-source-file="error-boundary.tsx" />;
      }

      // Defensive: if the card itself throws during render, the
      // boundary can't catch itself — fall through to the slim UI.
      try {
        return <ErrorFeedbackCard eventId={this.state.eventId} onTryAgain={this.handleReset} data-sentry-element="ErrorFeedbackCard" data-sentry-component="ErrorBoundary" data-sentry-source-file="error-boundary.tsx" />;
      } catch {
        return <SlimFallback onTryAgain={this.handleReset} data-sentry-element="SlimFallback" data-sentry-component="ErrorBoundary" data-sentry-source-file="error-boundary.tsx" />;
      }
    }
    return this.props.children;
  }
}
function SlimFallback(t0) {
  const $ = _c(4);
  const {
    onTryAgain
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>;
    t2 = <p className="mt-2 text-gray-600">We’ve been automatically notified. Please refresh the page to try again.</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onTryAgain) {
    t3 = <div className="flex min-h-screen items-center justify-center" data-sentry-component="SlimFallback" data-sentry-source-file="error-boundary.tsx"><div className="text-center">{t1}{t2}<button onClick={onTryAgain} className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">Try Again</button></div></div>;
    $[2] = onTryAgain;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * Hook-based component that handles global promise rejections and fetch errors
 */
export function GlobalErrorHandler({
  children
}: {
  children: ReactNode;
}) {
  const pathname = usePathname();
  const isClerkDegraded = useClerkDegraded();
  const isAnonAuthed = useIsAnonAuthed();
  const isAnonAuthPending = useIsAnonAuthPending();

  // Check if current route uses auto-signin (don't redirect on 401)
  const isAutoSigninRoute = AUTO_SIGNIN_PREFIXES.some(prefix => pathname.startsWith(prefix));

  // Don't redirect on 401 when:
  // - auto-signin route (the page handles its own auth)
  // - Clerk is degraded (401 is likely the outage, not signed-out)
  // - already on an auth route (redirect would loop)
  // - the request is anon-authed (valid anon flow-token cookie) —
  //   protected endpoints 401 the anon user, but the user should
  //   stay on the page and use the in-app sign-in modal when they
  //   click a protected feature.
  const shouldSkipAuthRedirect = isAutoSigninRoute || isClerkDegraded || isAuthRoutePath(pathname) || isAnonAuthPending || isAnonAuthed === true;

  // Log only on transition to degraded (not on every re-render).
  const prevDegradedRef = useRef(false);
  useEffect(() => {
    if (isClerkDegraded && !prevDegradedRef.current) {
      logInfo('Clerk is degraded — 401 → login redirects are suppressed');
    }
    prevDegradedRef.current = isClerkDegraded;
  }, [isClerkDegraded]);

  // Effect re-installs the fetch wrapper on `pathname` change so the
  // closure always uses the current path. A fetch in flight at the
  // moment of navigation is evaluated against the path captured when
  // it was started.
  useEffect(() => {
    // Handle unhandled promise rejections — only for auth-error redirect.
    // Exception capture is delegated to Sentry's globalHandlersIntegration
    // (when Sentry is on) and PostHog's capture_unhandled_rejections (when
    // Sentry is off, gated in bootstrap-client.ts). Adding a third manual
    // capture here would create duplicate events in both systems.
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (isAuthError(error) && !shouldSkipAuthRedirect) {
        event.preventDefault(); // Prevent the default browser error handling

        const loginUrl = `${ALL_ROUTES.LOGIN}?redirectedFrom=${encodeURIComponent(pathname)}`;
        redirectToLoginAfterCacheClear(loginUrl);
      }
    };

    // Handle global fetch errors
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        // Same-origin /api only — Clerk FAPI / PostgREST 401s
        // must not bounce the signed-in user to /login.
        if (response.status === 401 && !shouldSkipAuthRedirect && isSameOriginApiFetch(args[0])) {
          // A lapsed Clerk session (`session-token-unavailable`) leaves
          // the client still looking signed in, so a plain /login
          // redirect ping-pongs back; `reauthRequired` makes the login
          // page render the form instead.
          const denialCode = response.headers.get(API_401_HEADER.DENIAL_CODE);
          const loginUrl_1 = denialCode === 'session-token-unavailable' ? buildSignInRedirect(pathname, {
            reauthRequired: true
          }) : `${ALL_ROUTES.LOGIN}?redirectedFrom=${encodeURIComponent(pathname)}`;
          redirectToLoginAfterCacheClear(loginUrl_1);

          // Still return the response so the calling code can handle it if needed
          return response;
        }
        return response;
      } catch (error_0) {
        if (isAuthError(error_0) && !shouldSkipAuthRedirect) {
          const loginUrl_0 = `${ALL_ROUTES.LOGIN}?redirectedFrom=${encodeURIComponent(pathname)}`;
          redirectToLoginAfterCacheClear(loginUrl_0);
        }
        throw error_0;
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.fetch = originalFetch; // Restore original fetch
    };
  }, [pathname, shouldSkipAuthRedirect]);
  return <>{children}</>;
}
export function isAuthError(error: unknown): boolean {
  if (!error) return false;

  // Native fetch Response object
  if (error instanceof Response) return error.status === 401;
  const e = error as ErrorWithStatus;
  return e.status === 401 || e.response?.status === 401 || e.name === 'AuthenticationError' || e.name === 'UnauthorizedError';
}

/**
 * Complete Error Boundary wrapper that includes both class-based and hook-based error handling
 */
export function AppErrorBoundary(t0) {
  const $ = _c(6);
  const {
    children,
    fallback
  } = t0;
  const isClerkDegraded = useClerkDegraded();
  let t1;
  if ($[0] !== children) {
    t1 = <GlobalErrorHandler data-sentry-element="GlobalErrorHandler" data-sentry-source-file="error-boundary.tsx">{children}</GlobalErrorHandler>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== fallback || $[3] !== isClerkDegraded || $[4] !== t1) {
    t2 = <ErrorBoundary fallback={fallback} isClerkDegraded={isClerkDegraded} data-sentry-element="ErrorBoundary" data-sentry-component="AppErrorBoundary" data-sentry-source-file="error-boundary.tsx">{t1}</ErrorBoundary>;
    $[2] = fallback;
    $[3] = isClerkDegraded;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}